import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
import { useIntl } from "react-intl";
import useLewooPayWalletAccess from "../../Hooks/useLewooPayWalletAccess";
import ProfileApi from "../../Services/Profile";
import TriplistApi from "../../Services/Triplist";
import ratingsApi from "../../Services/Ratings";
import WalletApi from "../../Services/Wallet";
import "./HomePage.css";

function formatFCFA(amount) {
  const num = parseFloat(amount) || 0;
  return Math.round(num).toLocaleString("fr-FR").replace(/ /g, " ");
}

function formatCount(amount) {
  const num = parseFloat(amount) || 0;
  return Math.round(num).toLocaleString("fr-FR").replace(/ /g, " ");
}

function toNumber(value, fallback = 0) {
  const num = Number(value);
  return Number.isFinite(num) ? num : fallback;
}

function firstText(...values) {
  for (const value of values) {
    if (value === null || value === undefined) {
      continue;
    }

    const text = String(value).trim();
    if (text) {
      return text;
    }
  }

  return "";
}

function normalizeName(value) {
  return String(value || "")
    .trim()
    .replace(/\s+/g, " ")
    .toLowerCase();
}

function extractListFromResponse(response) {
  const payload = response?.data?.data ?? response?.data ?? response ?? {};

  const candidates = [
    payload?.data?.data,
    payload?.data?.results,
    payload?.data?.rides,
    payload?.data?.bookings,
    payload?.data,
    payload?.results,
    payload?.rides,
    payload?.bookings,
    payload,
  ];

  return candidates.find((value) => Array.isArray(value)) || [];
}

function extractObjectFromResponse(response) {
  const payload = response?.data?.data ?? response?.data ?? response ?? {};
  return Array.isArray(payload) ? {} : payload;
}

function formatDateTime(value) {
  if (!value) {
    return "--";
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "--";
  }

  return new Intl.DateTimeFormat("fr-FR", {
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  })
    .format(date)
    .replace(",", "");
}

function formatShortDate(value) {
  if (!value) {
    return "--";
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "--";
  }

  return new Intl.DateTimeFormat("fr-FR", {
    day: "2-digit",
    month: "short",
  })
    .format(date)
    .replace(/\./g, "");
}

function formatTimeOnly(value) {
  if (!value) {
    return "--:--";
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "--:--";
  }

  return new Intl.DateTimeFormat("fr-FR", {
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
}

function formatTimeUntil(value, t) {
  if (!value) {
    return "";
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "";
  }

  const diffMinutes = Math.round((date.getTime() - Date.now()) / 60000);

  if (diffMinutes <= 0) {
    return t("dashboard.time.inProgress");
  }

  if (diffMinutes < 60) {
    return t("dashboard.time.inMinutes", { minutes: diffMinutes });
  }

  const hours = Math.floor(diffMinutes / 60);
  const minutes = diffMinutes % 60;

  if (hours < 24) {
    return minutes > 0
      ? t("dashboard.time.inHoursMinutes", { hours, minutes: String(minutes).padStart(2, "0") })
      : t("dashboard.time.inHours", { hours });
  }

  const days = Math.floor(hours / 24);
  return days === 1 ? t("dashboard.time.tomorrow") : t("dashboard.time.inDays", { days });
}

function formatDuration(minutes) {
  const totalMinutes = Math.max(0, Math.round(toNumber(minutes)));
  if (!totalMinutes) {
    return "--";
  }

  const hours = Math.floor(totalMinutes / 60);
  const remaining = totalMinutes % 60;
  return hours > 0 ? `${hours}h${String(remaining).padStart(2, "0")}` : `${remaining} min`;
}

function humanizeStatus(status, t) {
  const value = String(status || "").trim().toLowerCase();

  if (value === "completed") return t("dashboard.status.completed");
  if (value === "cancelled" || value === "canceled") return t("dashboard.status.cancelled");
  if (value === "confirmed") return t("dashboard.status.confirmed");
  if (value === "pending") return t("dashboard.status.pending");
  if (value === "scheduled" || value === "planned") return t("dashboard.status.scheduled");
  if (value === "approved") return t("dashboard.status.approved");
  if (value === "rejected") return t("dashboard.status.rejected");

  return status ? String(status) : t("dashboard.status.default");
}

function buildLevelLabel(points, t) {
  const totalPoints = toNumber(points, 0);

  if (totalPoints >= 1500) return t("dashboard.level.gold");
  if (totalPoints >= 750) return t("dashboard.level.silver");
  if (totalPoints > 0) return t("dashboard.level.bronze");
  return t("dashboard.level.new");
}

function normalizeDriverRide(item, index, t) {
  const departureAt = firstText(
    item?.departure_at,
    item?.acceptedAt,
    item?.startedAt,
    item?.createdAt,
    item?.created_at
  );

  const origin = firstText(
    item?.pickupAddress,
    item?.pickup_address,
    item?.pickupPlace,
    item?.originLabel,
    item?.origin_place_name,
    item?.origin_place?.name,
    item?.origin?.addressName,
    item?.origin?.name,
    item?.from
  );

  const destination = firstText(
    item?.dropAddress,
    item?.drop_address,
    item?.dropoffPlace,
    item?.destinationLabel,
    item?.destination_place_name,
    item?.destination_place?.name,
    item?.destination?.addressName,
    item?.destination?.name,
    item?.to
  );

  const paymentMode = firstText(item?.paymentMode, item?.payment_method, item?.paymentType, item?.payment);
  const status = firstText(item?.tripStatus, item?.status, item?.rideStatus, item?.bookingStatus);
  const earnings = toNumber(
    firstText(
      item?.driverEarnings?.earnings,
      item?.driverEarnings,
      item?.earnings,
      item?.total_amount,
      item?.totalAmount,
      item?.estimatedFare
    )
  );
  const total = toNumber(firstText(item?.estimatedFare, item?.total, item?.fare, item?.price, item?.amount));
  const distance = toNumber(firstText(item?.tripDistance, item?.distance, item?.trip_distance));
  const duration = toNumber(firstText(item?.tripMinutes, item?.duration, item?.trip_duration));
  const stops = toNumber(firstText(item?.stops_count, item?.stopCount, item?.waypoints?.length, item?.ride_waypoints?.length));
  const tripId = firstText(item?.bookingId, item?.booking_id, item?.id, item?._id, `${index + 1}`);

  return {
    id: tripId,
    bookingId: tripId,
    routeLabel: origin || destination ? `${origin || t("dashboard.route.departure")} → ${destination || t("dashboard.route.arrival")}` : t("dashboard.route.noRoute"),
    origin,
    destination,
    departureAt,
    status,
    statusLabel: humanizeStatus(status, t),
    paymentMode,
    earnings,
    total,
    distance,
    duration,
    stops,
    raw: item,
  };
}

function isUpcomingRide(ride) {
  const status = String(ride?.status || "").toLowerCase();
  if (["completed", "cancelled", "canceled"].includes(status)) {
    return false;
  }

  if (["confirmed", "pending", "scheduled", "planned"].includes(status)) {
    return true;
  }

  if (!ride?.departureAt) {
    return false;
  }

  const departure = new Date(ride.departureAt);
  return !Number.isNaN(departure.getTime()) && departure.getTime() >= Date.now();
}

function buildDestinationStats(rides) {
  if (!rides.length) {
    return [];
  }

  const counts = new Map();

  rides.forEach((ride) => {
    const key = firstText(ride?.destination, ride?.origin);
    if (!key) {
      return;
    }
    counts.set(key, (counts.get(key) || 0) + 1);
  });

  const total = rides.length || 1;

  return [...counts.entries()]
    .sort((a, b) => b[1] - a[1])
    .slice(0, 5)
    .map(([name, count]) => ({
      name,
      count,
      percent: Math.max(3, Math.round((count / total) * 100)),
    }));
}

function buildRatingBreakdown(ratings) {
  if (!ratings.length) {
    return [];
  }

  return [5, 4, 3, 2, 1].map((score) => ({
    label: `${score} ★`,
    value: ratings.filter((rating) => Math.round(toNumber(rating?.rating)) === score).length,
  }));
}

function getAverageRating(ratings = []) {
  if (!ratings.length) {
    return null;
  }

  const sum = ratings.reduce((acc, rating) => acc + toNumber(rating?.rating), 0);
  return sum / ratings.length;
}

function matchesDriverRecord(record, driverId, driverRideId, displayName) {
  if (!record) {
    return false;
  }

  const recordIds = [
    record?._id,
    record?.id,
    record?.userId,
    record?.user_id,
    record?.driver_id,
    record?.driver_ride_details?._id,
    record?.driver_ride_details?.id,
    record?.driver_ride_details?.driver_id,
    record?.driverRideDetails?._id,
    record?.driverRideDetails?.id,
    record?.driverRideDetails?.driver_id,
  ]
    .map((value) => String(value || "").trim())
    .filter(Boolean);

  if (driverId && recordIds.includes(String(driverId).trim())) {
    return true;
  }

  if (driverRideId && recordIds.includes(String(driverRideId).trim())) {
    return true;
  }

  const recordName = normalizeName(
    firstText(
      record?.name,
      `${record?.firstName || record?.first_name || ""} ${record?.lastName || record?.last_name || ""}`.trim(),
      record?.user_name,
      record?.userdetails?.user_name
    )
  );

  return Boolean(recordName) && recordName === normalizeName(displayName);
}

function LockIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <rect x="5" y="10" width="14" height="10" rx="2.5" />
      <path d="M8.5 10V8.5a3.5 3.5 0 1 1 7 0V10" />
    </svg>
  );
}

function BagIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M7 8h10l1 11H6L7 8Z" />
      <path d="M9 8a3 3 0 0 1 6 0" />
      <path d="M10 11h4" />
    </svg>
  );
}

function WalletIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M4 8.5A2.5 2.5 0 0 1 6.5 6h11A2.5 2.5 0 0 1 20 8.5V17a2 2 0 0 1-2 2H6.5A2.5 2.5 0 0 1 4 16.5v-8Z" />
      <path d="M15.5 12.5h4" />
    </svg>
  );
}

function StarIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="m12 4 2.6 5.2 5.8.8-4.2 4.1 1 5.8L12 17.7 6.8 20l1-5.8L3.6 10l5.8-.8L12 4Z" />
    </svg>
  );
}

function CrownIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M4 7.5 7.5 11l4.5-5 4.5 5L20 7.5 18.5 17H5.5L4 7.5Z" />
      <path d="M6.5 17h11" />
    </svg>
  );
}

function HeartIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M12 20s-7-4.5-7-9.5A4.5 4.5 0 0 1 12 7a4.5 4.5 0 0 1 7 3.5C19 15.5 12 20 12 20Z" />
    </svg>
  );
}

function RoutesIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M6 6h4a3 3 0 0 1 3 3v0a3 3 0 0 1-3 3H8a2 2 0 0 0-2 2v0a2 2 0 0 0 2 2h10" />
      <path d="M15 18l3-3-3-3" />
    </svg>
  );
}

function ClockIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <circle cx="12" cy="12" r="8.5" />
      <path d="M12 7.5v5l3 2" />
    </svg>
  );
}

function ChevronDownIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="m6 9 6 6 6-6" />
    </svg>
  );
}

function PlusCircleIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <circle cx="12" cy="12" r="9" />
      <path d="M12 8v8M8 12h8" />
    </svg>
  );
}

function EmptyState({ title, description }) {
  return (
    <div className="dbc-empty-state">
      <strong>{title}</strong>
      <span>{description}</span>
    </div>
  );
}

function MetricCard({ item, locked = false, blocked = false }) {
  const intl = useIntl();
  const t = (id, values) => intl.formatMessage({ id }, values);
  const isMuted = locked || blocked;

  return (
    <article className={`dbc-metric-card ${isMuted ? "is-locked" : ""}`}>
      <div className={`dbc-metric-icon ${isMuted ? "is-locked" : ""}`}>
        {isMuted ? <LockIcon /> : null}
        {!isMuted && item.icon === "bag" ? <BagIcon /> : null}
        {!isMuted && item.icon === "wallet" ? <WalletIcon /> : null}
        {!isMuted && item.icon === "star" ? <StarIcon /> : null}
        {!isMuted && item.icon === "badge" ? <CrownIcon /> : null}
      </div>
      <div className="dbc-metric-copy">
        <span>{item.label}</span>
        <strong>{isMuted ? "—" : item.value}</strong>
        <small>{blocked ? t("dashboard.metric.walletRestricted") : locked ? t("dashboard.metric.accountRequired") : item.meta}</small>
      </div>
    </article>
  );
}

function TimelineLine() {
  return (
    <div className="dbc-timeline-line" aria-hidden="true">
      <span />
      <span />
    </div>
  );
}

function NextTripCard({ trip, loading = false }) {
  const intl = useIntl();
  const t = (id, values) => intl.formatMessage({ id }, values);
  const hasTrip = Boolean(trip);
  const departureAtLabel = hasTrip ? formatTimeOnly(trip.departureAt) : "";
  const dateLabel = hasTrip ? formatShortDate(trip.departureAt) : "";
  const timeUntil = hasTrip ? formatTimeUntil(trip.departureAt, t) : "";
  const durationLabel = hasTrip ? formatDuration(trip.duration) : "";
  const stopsCount = hasTrip ? toNumber(trip.stops) : 0;
  const stopsLabel = stopsCount > 0 ? t("dashboard.stops", { count: stopsCount }) : "";

  return (
    <article className="dbc-panel">
      <div className="dbc-panel-head">
        <h3>
          <span className="dbc-panel-title-icon">
            <RoutesIcon />
          </span>
          {t("dashboard.nextTrip.title")}
        </h3>
        <span className="dbc-pill">{loading ? t("dashboard.nextTrip.syncing") : hasTrip ? timeUntil || t("dashboard.nextTrip.found") : t("dashboard.nextTrip.noTrip")}</span>
      </div>

      {hasTrip ? (
        <div className="dbc-next-trip">
          <div className="dbc-time-col">
            <strong>{departureAtLabel}</strong>
            <span>{durationLabel}</span>
          </div>

          <TimelineLine />

          <div className="dbc-route-col">
            <div className="dbc-route-stop">
              <strong>{trip.origin || t("dashboard.route.departure")}</strong>
              <span>{dateLabel}</span>
            </div>
            <div className="dbc-route-stops">
              <span>{stopsLabel || t("dashboard.noStops")}</span>
              <ChevronDownIcon />
            </div>
            <div className="dbc-route-stop">
              <strong>{trip.destination || t("dashboard.route.arrival")}</strong>
              <span>{trip.paymentMode || t("dashboard.nextTrip.paymentOnBoard")}</span>
            </div>
          </div>

          <div className="dbc-time-col dbc-time-col-end">
            <strong>{trip.statusLabel || t("dashboard.status.default")}</strong>
            <span>{trip.routeLabel || ""}</span>
          </div>
        </div>
      ) : (
        <EmptyState
          title={t("dashboard.nextTrip.noNextTrip")}
          description={loading ? t("dashboard.nextTrip.syncingDesc") : t("dashboard.nextTrip.noTripDesc")}
        />
      )}

      <div className="dbc-panel-actions">
        <Link to="/trajet-driver" className="dbc-outline-btn">{t("dashboard.nextTrip.viewDetails")}</Link>
        <Link to="/course-driver" className="dbc-solid-btn">{t("dashboard.nextTrip.manage")}</Link>
      </div>
    </article>
  );
}

function WalletCard({ blocked = false, locked = false, status = "checking", balance = null, balanceLoading = false }) {
  const intl = useIntl();
  const t = (id, values) => intl.formatMessage({ id }, values);
  const isDisabled = locked || blocked;
  const balanceDisplay = balanceLoading
    ? "…"
    : balance !== null
      ? formatFCFA(balance)
      : "—";

  const statusCopy =
    blocked
      ? {
          eyebrow: t("dashboard.wallet.unavailable"),
          value: t("dashboard.wallet.accessRestricted"),
          note: t("dashboard.wallet.contentHidden"),
        }
      : status === "pending"
        ? {
            eyebrow: t("dashboard.wallet.locked"),
            value: t("dashboard.wallet.pendingValue"),
            note: t("dashboard.wallet.pendingNote"),
          }
        : status === "approved-needs-creation"
          ? {
              eyebrow: t("dashboard.wallet.locked"),
              value: t("dashboard.wallet.needsCreationValue"),
              note: t("dashboard.wallet.needsCreationNote"),
            }
          : status === "error"
            ? {
                eyebrow: t("dashboard.wallet.locked"),
                value: t("dashboard.wallet.errorValue"),
                note: t("dashboard.wallet.errorNote"),
              }
            : locked
              ? {
                  eyebrow: t("dashboard.wallet.locked"),
                  value: t("dashboard.wallet.checkingValue"),
                  note: t("dashboard.wallet.checkingNote"),
                }
              : {
                  eyebrow: t("dashboard.wallet.available"),
                  value: balanceDisplay,
                  note: t("dashboard.wallet.fcfa"),
                };

  return (
    <article className={`dbc-panel dbc-wallet-panel ${blocked ? "is-muted" : ""}`.trim()}>
      <div className="dbc-panel-head">
        <h3>{t("dashboard.wallet.title")}</h3>
        {isDisabled ? (
          <span className="dbc-wallet-locked-chip">
            <LockIcon />
            {blocked ? t("dashboard.wallet.restricted") : t("dashboard.wallet.locked")}
          </span>
        ) : (
          <a href="/">{t("dashboard.wallet.viewAll")}</a>
        )}
      </div>
      <div className={`dbc-wallet-hero ${isDisabled ? "is-locked" : ""}`}>
        <div>
          <span>{statusCopy.eyebrow}</span>
          <strong>{isDisabled ? statusCopy.value : balanceDisplay}</strong>
          <small>{statusCopy.note}</small>
        </div>
        <div className={`dbc-wallet-art ${isDisabled ? "is-locked" : ""}`} aria-hidden="true">
          <div className="dbc-wallet-outline" />
          {isDisabled ? (
            <div className="dbc-wallet-lock" aria-hidden="true">
              <LockIcon />
            </div>
          ) : null}
        </div>
      </div>
      {isDisabled ? (
        <p className="dbc-wallet-lock-note">
          <LockIcon />
          <span>{statusCopy.note}</span>
        </p>
      ) : null}
      <div className={`dbc-wallet-actions ${isDisabled ? "is-locked" : ""}`}>
        <button type="button" disabled={isDisabled}>
          <span className="dbc-mini-circle">{isDisabled ? <LockIcon /> : "+"}</span>
          {t("dashboard.wallet.addMoney")}
        </button>
        <button type="button" disabled={isDisabled}>
          <span className="dbc-mini-circle">{isDisabled ? <LockIcon /> : "↗"}</span>
          {t("dashboard.wallet.withdraw")}
        </button>
        <button type="button" disabled={isDisabled}>
          <span className="dbc-mini-circle">{isDisabled ? <LockIcon /> : "◌"}</span>
          {t("dashboard.wallet.history")}
        </button>
      </div>
    </article>
  );
}

function BadgesCard({ badges }) {
  const intl = useIntl();
  const t = (id, values) => intl.formatMessage({ id }, values);
  const hasBadges = badges.length > 0;

  return (
    <article className="dbc-panel">
      <div className="dbc-panel-head">
        <h3>{t("dashboard.badge.title")}</h3>
        <Link to="/profile-driver">{t("dashboard.badge.viewAll")}</Link>
      </div>
      {hasBadges ? (
        <div className="dbc-badges">
          {badges.map((badge) => (
            <div key={badge.title} className="dbc-badge-row">
              <div className={`dbc-badge-icon is-${badge.tone}`}>
                {badge.icon === "crown" ? <CrownIcon /> : null}
                {badge.icon === "star" ? <StarIcon /> : null}
                {badge.icon === "heart" ? <HeartIcon /> : null}
              </div>
              <div>
                <strong>{badge.title}</strong>
                <span>{badge.desc}</span>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <EmptyState
          title={t("dashboard.badge.noBadges")}
          description={t("dashboard.badge.noBadgesDesc")}
        />
      )}
    </article>
  );
}

function HistoryCard({ rides }) {
  const intl = useIntl();
  const t = (id, values) => intl.formatMessage({ id }, values);
  const hasRides = rides.length > 0;

  return (
    <article className="dbc-panel">
      <div className="dbc-panel-head">
        <h3>
          <span className="dbc-panel-title-icon">
            <ClockIcon />
          </span>
          {t("dashboard.history.title")}
        </h3>
        <Link to="/trajet-driver">{t("dashboard.history.viewAll")}</Link>
      </div>
      {hasRides ? (
        <div className="dbc-history-list">
          {rides.map((trip) => {
            const dateLabel = formatShortDate(trip.departureAt);
            const dateParts = dateLabel.split(" ");
            const day = dateParts[0] || "--";
            const month = dateParts[1] || "";
            const routeLabel = trip.routeLabel || [trip.origin, trip.destination].filter(Boolean).join(" → ");

            return (
              <div key={`${trip.id}-${trip.bookingId}`} className="dbc-history-row">
                <div className="dbc-history-date">
                  <strong>{day}</strong>
                  <span>{month}</span>
                </div>
                <div className="dbc-history-route">
                  <strong>{routeLabel || t("dashboard.route.noRoute")}</strong>
                  <span>
                    {trip.departureAt ? formatTimeOnly(trip.departureAt) : "—"}
                    {trip.statusLabel ? ` • ${trip.statusLabel}` : ""}
                  </span>
                </div>
                <div className="dbc-history-price">
                  <strong>{trip.earnings !== undefined && trip.earnings !== null ? `${formatFCFA(trip.earnings)} FCFA` : "—"}</strong>
                  <span>{trip.paymentMode || "—"}</span>
                </div>
                <Link to="/trajet-driver" className="dbc-evaluate-btn">{t("dashboard.history.view")}</Link>
              </div>
            );
          })}
        </div>
      ) : (
        <EmptyState
          title={t("dashboard.history.noHistory")}
          description={t("dashboard.history.noHistoryDesc")}
        />
      )}
    </article>
  );
}

function DestinationsCard({ destinations }) {
  const intl = useIntl();
  const t = (id, values) => intl.formatMessage({ id }, values);
  const hasDestinations = destinations.length > 0;

  return (
    <article className="dbc-panel">
      <div className="dbc-panel-head">
        <h3>{t("dashboard.destination.title")}</h3>
        <Link to="/trajet-driver">{t("dashboard.destination.viewAll")}</Link>
      </div>
      {hasDestinations ? (
        <div className="dbc-progress-list">
          {destinations.map((destination) => (
            <div key={destination.name} className="dbc-progress-row">
              <div className="dbc-progress-label">
                <span className="dbc-dot" />
                <strong>{destination.name}</strong>
              </div>
              <div className="dbc-progress-bar">
                <span style={{ width: `${destination.percent}%` }} />
              </div>
              <strong className="dbc-progress-value">{destination.percent}%</strong>
            </div>
          ))}
        </div>
      ) : (
        <EmptyState
          title={t("dashboard.destination.noDestinations")}
          description={t("dashboard.destination.noDestinationsDesc")}
        />
      )}
    </article>
  );
}

function RatingsCard({ averageRating, ratingCount, ratings }) {
  const intl = useIntl();
  const t = (id, values) => intl.formatMessage({ id }, values);
  const hasSummary = averageRating !== null && averageRating !== undefined;
  const hasRatings = ratings.length > 0;
  const summaryValue = hasSummary ? `${averageRating.toFixed(1)} / 5` : "—";

  return (
    <article className="dbc-panel">
      <div className="dbc-panel-head">
        <h3>{t("dashboard.rating.title")}</h3>
        <Link to="/driverratings">{t("dashboard.rating.viewAll")}</Link>
      </div>
      <div className="dbc-rating-summary">
        {hasSummary || ratingCount > 0 ? (
          <>
            <strong>{summaryValue}</strong>
            <span>{ratingCount > 0 ? t("dashboard.rating.basedOn", { count: ratingCount }) : t("dashboard.rating.syncedData")}</span>
            <div className="dbc-stars">★★★★★</div>
          </>
        ) : (
          <EmptyState
            title={t("dashboard.rating.noRating")}
            description={t("dashboard.rating.noRatingDesc")}
          />
        )}
      </div>
      {hasRatings ? (
        <div className="dbc-rating-bars">
          {ratings.map((rating) => (
            <div key={rating.label} className="dbc-rating-row">
              <span>{rating.label}</span>
              <div className="dbc-progress-bar">
                <span style={{ width: `${Math.max(rating.value * 5, 4)}%` }} />
              </div>
              <strong>{rating.value}</strong>
            </div>
          ))}
        </div>
      ) : null}
    </article>
  );
}

export default function DashboardDriver() {
  const intl = useIntl();
  const t = (id, values) => intl.formatMessage({ id }, values);
  const { walletLocked, walletStatus } = useLewooPayWalletAccess();
  const lewooPayToken = useSelector(
    (state) => state?.AuthReducer?.lewooPayToken || localStorage.getItem("lewooPayToken") || ""
  );
  const authUser = useSelector((state) => state?.AuthReducer?.user || {});
  const resolvedToken = String(lewooPayToken || "").trim();
  const resolvedUserId = String(
    authUser?.driver_ride_details?.driver_id ||
    authUser?.driverRideDetails?.driver_id ||
    authUser?.driver_id ||
    authUser?._id ||
    authUser?.id ||
    localStorage.getItem("userid") ||
    ""
  ).trim();
  const resolvedDriverRideId = String(
    authUser?.driver_ride_details?._id ||
    authUser?.driverRideDetails?._id ||
    authUser?.driver_ride_details?.id ||
    authUser?.driverRideDetails?.id ||
    ""
  ).trim();

  const resolvedUser = {
    firstName: authUser?.firstName || authUser?.first_name || "",
    lastName: authUser?.lastName || authUser?.last_name || "",
    email: authUser?.email || "",
    phone: authUser?.phone || "",
    avatar: authUser?.avatar_url || authUser?.image_local || "",
  };

  const displayName =
    authUser?.name ||
    `${resolvedUser.firstName} ${resolvedUser.lastName}`.trim() ||
    t("dashboard.user.defaultName");

  const [walletBalance, setWalletBalance] = useState(null);
  const [balanceLoading, setBalanceLoading] = useState(false);
  const [totalDepenses, setTotalDepenses] = useState(null);
  const [walletAccessBlocked, setWalletAccessBlocked] = useState(false);

  const [driverRecord, setDriverRecord] = useState(null);
  const [driverRides, setDriverRides] = useState([]);
  const [profileStats, setProfileStats] = useState(null);
  const [driverRatings, setDriverRatings] = useState([]);
  const [dashboardLoading, setDashboardLoading] = useState(false);
  const [dashboardError, setDashboardError] = useState("");
  const [lastRefreshedAt, setLastRefreshedAt] = useState(null);

  useEffect(() => {
    let isActive = true;

    const loadWalletSummary = async () => {
      if (!resolvedToken) {
        setWalletAccessBlocked(false);
        setWalletBalance(null);
        setTotalDepenses(null);
        setBalanceLoading(false);
        return;
      }

      setBalanceLoading(true);
      setWalletAccessBlocked(false);
      setWalletBalance(null);
      setTotalDepenses(null);

      try {
        const walletsResponse = await WalletApi.getClientWallets(resolvedToken, {
          suppressForbiddenRedirect: true,
        });

        if (!isActive) {
          return;
        }

        const wallets = walletsResponse?.data?.data || [];
        const primary = wallets.find((w) => w.is_primary) || wallets[0] || null;

        if (!primary) {
          return;
        }

        const walletId = primary.id;
        const syncResponse = await WalletApi.syncClientWallet(walletId, resolvedToken, {
          suppressForbiddenRedirect: true,
        });

        if (!isActive) {
          return;
        }

        const data = syncResponse?.data?.data || {};
        setWalletBalance(data.balance ?? 0);

        const transactionsResponse = await WalletApi.getClientWalletTransactions(walletId, resolvedToken, {}, {
          suppressForbiddenRedirect: true,
        });

        if (!isActive) {
          return;
        }

        const txs = transactionsResponse?.data?.data || [];
        const total = txs
          .filter((t) => t.debit === true || t.entryType === "DEBIT")
          .reduce((sum, t) => sum + (parseFloat(t.amount) || 0), 0);
        if (total > 0) {
          setTotalDepenses(total);
        }
      } catch (error) {
        if (!isActive) {
          return;
        }

        if (error?.response?.status === 403) {
          setWalletAccessBlocked(true);
          setWalletBalance(null);
          setTotalDepenses(null);
        }
      } finally {
        if (isActive) {
          setBalanceLoading(false);
        }
      }
    };

    loadWalletSummary();

    return () => {
      isActive = false;
    };
  }, [resolvedToken]);

  useEffect(() => {
    let isActive = true;

    const loadDriverOverview = async () => {
      if (!resolvedUserId) {
        if (isActive) {
          setDriverRecord(null);
          setDriverRides([]);
          setProfileStats(null);
          setDriverRatings([]);
          setDashboardError(t("dashboard.error.noDriverId"));
          setLastRefreshedAt(null);
        }
        return;
      }

      if (isActive) {
        setDashboardLoading(true);
        setDashboardError("");
      }

      try {
        const [driversResult, statsResult, ratingsResult] = await Promise.allSettled([
          TriplistApi.getdriversinfo({ page: 1, limit: "unlimited" }),
          ProfileApi.getUserStatisticsById(resolvedUserId),
          ratingsApi.getDriverratingsList({ page: 1, limit: "unlimited" }),
        ]);

        const driversList = driversResult.status === "fulfilled" ? extractListFromResponse(driversResult.value) : [];
        const matchedDriver = driversList.find((record) => matchesDriverRecord(record, resolvedUserId, resolvedDriverRideId, displayName))
          || driversList[0]
          || null;

        const statsObject = statsResult.status === "fulfilled" ? extractObjectFromResponse(statsResult.value) : null;
        const ratingsList = ratingsResult.status === "fulfilled" ? extractListFromResponse(ratingsResult.value) : [];

        const driverRideId = firstText(
          resolvedDriverRideId,
          matchedDriver?.driver_ride_details?._id,
          matchedDriver?.driver_ride_details?.id,
          matchedDriver?.driverRideDetails?._id,
          matchedDriver?.driverRideDetails?.id
        );

        let ridesSource = Array.isArray(matchedDriver?.ridesaccepted) ? matchedDriver.ridesaccepted : [];
        let resolvedDriverRecord = matchedDriver;

        if (driverRideId) {
          try {
            const ridesResponse = await TriplistApi.getridesofdriver({
              page: 1,
              limit: "unlimited",
              driver_ride_id: driverRideId,
              driver_id: resolvedUserId,
              filtervalues: "",
            });
            const ridesPayload = extractObjectFromResponse(ridesResponse);
            const apiRides = extractListFromResponse(ridesResponse);

            ridesSource = apiRides.length ? apiRides : ridesSource;

            if (isActive && ridesPayload) {
              const normalizedCount = Array.isArray(ridesSource) ? ridesSource.length : 0;
              const totalRevenue = toNumber(
                ridesPayload?.totalRevenue ??
                ridesPayload?.revenue ??
                ridesPayload?.total_revenue ??
                ridesPayload?.data?.totalRevenue ??
                ridesPayload?.data?.revenue
              );
              const cancelledRides = toNumber(
                ridesPayload?.cancelledRides ??
                ridesPayload?.cancelled_count ??
                ridesPayload?.data?.cancelledRides
              );

              if (normalizedCount > 0 || totalRevenue > 0 || cancelledRides > 0) {
                resolvedDriverRecord = {
                  ...(matchedDriver || {}),
                  ridesaccepted: ridesSource,
                  driverRideMetrics: {
                    totalRidesCount: toNumber(
                      ridesPayload?.totalRidesCount ??
                      ridesPayload?.totalRecords ??
                      normalizedCount
                    ),
                    totalRevenue,
                    cancelledRides,
                  },
                };
              }
            }
          } catch (rideError) {
            if (isActive && ridesSource.length === 0) {
              ridesSource = Array.isArray(matchedDriver?.ridesaccepted) ? matchedDriver.ridesaccepted : [];
            }
          }
        }

        const normalizedRides = ridesSource.map((ride, index) => normalizeDriverRide(ride, index, t));
        const normalizedRatings = ratingsList.filter((rating) => {
          const ratingDriverId = firstText(
            rating?.driverdetails?._id,
            rating?.driverdetails?.id,
            rating?.driver_id,
            rating?.driverId
          );
          if (ratingDriverId && resolvedUserId && String(ratingDriverId).trim() === resolvedUserId) {
            return true;
          }

          const ratingName = normalizeName(
            firstText(
              rating?.driverdetails?.user_name,
              `${rating?.driverdetails?.first_name || ""} ${rating?.driverdetails?.last_name || ""}`.trim(),
              rating?.drivername,
              rating?.driver_name
            )
          );

          return Boolean(ratingName) && ratingName === normalizeName(displayName);
        });

        if (isActive) {
          setDriverRecord(resolvedDriverRecord);
          setDriverRides(normalizedRides);
          setProfileStats(statsObject);
          setDriverRatings(normalizedRatings);
          setLastRefreshedAt(new Date());
        }
      } catch (error) {
        if (isActive) {
          setDashboardError(error?.response?.data?.message || t("dashboard.error.loadFailed"));
        }
      } finally {
        if (isActive) {
          setDashboardLoading(false);
        }
      }
    };

    loadDriverOverview();

    return () => {
      isActive = false;
    };
  }, [displayName, resolvedDriverRideId, resolvedUserId]);

  const driverRideMetrics = driverRecord?.driverRideMetrics || {};

  const normalizedRides = useMemo(() => {
    return [...driverRides].sort((a, b) => {
      const aDate = new Date(a.departureAt || a.raw?.acceptedAt || a.raw?.createdAt || 0).getTime();
      const bDate = new Date(b.departureAt || b.raw?.acceptedAt || b.raw?.createdAt || 0).getTime();
      return bDate - aDate;
    });
  }, [driverRides]);

  const upcomingRides = useMemo(
    () => normalizedRides.filter(isUpcomingRide).sort((a, b) => {
      const aDate = new Date(a.departureAt || a.raw?.acceptedAt || a.raw?.createdAt || 0).getTime();
      const bDate = new Date(b.departureAt || b.raw?.acceptedAt || b.raw?.createdAt || 0).getTime();
      return aDate - bDate;
    }),
    [normalizedRides]
  );

  const pastRides = useMemo(
    () => normalizedRides.filter((ride) => !isUpcomingRide(ride)),
    [normalizedRides]
  );

  const nextTrip = upcomingRides[0] || normalizedRides[0] || null;
  const recentRides = (pastRides.length ? pastRides : normalizedRides).slice(0, 3);
  const destinationStats = useMemo(() => buildDestinationStats(pastRides.length ? pastRides : normalizedRides), [normalizedRides, pastRides]);
  const averageRating = useMemo(() => {
    const computedAverage = getAverageRating(driverRatings);
    if (computedAverage !== null) {
      return computedAverage;
    }

    const fromStats = profileStats?.average_rating ?? profileStats?.averageRating ?? authUser?.rating;
    return fromStats !== undefined && fromStats !== null && fromStats !== "" ? toNumber(fromStats, null) : null;
  }, [authUser?.rating, driverRatings, profileStats]);
  const ratingBreakdown = useMemo(() => buildRatingBreakdown(driverRatings), [driverRatings]);
  const ratingCount = driverRatings.length || 0;

  const completedTripsCount = useMemo(() => {
    const fromStats = profileStats?.completed_rides_count ?? profileStats?.completedRidesCount ?? authUser?.due_trip;
    const completedFromRides = normalizedRides.filter((ride) => String(ride.status).toLowerCase() === "completed").length;
    const fromDriverMetrics = driverRideMetrics?.totalRidesCount;

    if (fromStats !== undefined && fromStats !== null && fromStats !== "") {
      return toNumber(fromStats, completedFromRides || fromDriverMetrics || normalizedRides.length);
    }

    return completedFromRides || fromDriverMetrics || normalizedRides.length;
  }, [authUser?.due_trip, driverRideMetrics?.totalRidesCount, normalizedRides, profileStats]);

  const loyaltyPoints = useMemo(() => {
    const fromStats = profileStats?.loyalty_points ?? profileStats?.loyaltyPoints;
    const fromUser = authUser?.loyalty_points ?? authUser?.loyaltyPoints;
    const rawPoints = fromStats ?? fromUser;
    return rawPoints !== undefined && rawPoints !== null && rawPoints !== "" ? toNumber(rawPoints, null) : null;
  }, [authUser?.loyaltyPoints, authUser?.loyalty_points, profileStats]);

  const monthlyTrips = useMemo(() => {
    const now = new Date();
    return normalizedRides.filter((ride) => {
      if (!ride?.departureAt) {
        return false;
      }
      const date = new Date(ride.departureAt);
      return !Number.isNaN(date.getTime()) &&
        date.getMonth() === now.getMonth() &&
        date.getFullYear() === now.getFullYear();
    }).length;
  }, [normalizedRides]);

  const accountStatus = firstText(
    driverRecord?.driver_ride_details?.accountStatus,
    driverRecord?.driverRideDetails?.accountStatus,
    profileStats?.account_status,
    profileStats?.accountStatus
  );
  const accountStatusLabel = accountStatus ? humanizeStatus(accountStatus, t) : t("dashboard.account.statusUnavailable");
  const accountStatusTone = String(accountStatus || "").toLowerCase() === "approved" ? "is-active" : "is-locked";
  const lastRideLabel = recentRides[0] ? t("dashboard.account.lastRide", { date: formatShortDate(recentRides[0].departureAt) }) : t("dashboard.account.noRecentRide");
  const lastRefreshLabel = lastRefreshedAt ? t("dashboard.account.lastUpdate", { date: formatDateTime(lastRefreshedAt) }) : t("dashboard.account.pendingSync");
  const dashboardMetaLabel = dashboardLoading ? t("dashboard.account.syncing") : lastRefreshLabel;
  const loyaltyLevel = buildLevelLabel(loyaltyPoints, t);

  const dashboardMetrics = useMemo(() => {
    return [
      {
        label: t("dashboard.metric.trips"),
        value: formatCount(completedTripsCount),
        meta: monthlyTrips > 0 ? t("dashboard.metric.tripsThisMonth", { count: monthlyTrips }) : t("dashboard.metric.noTripsThisMonth"),
        icon: "bag",
      },
      {
        label: t("dashboard.metric.expenses"),
        value: totalDepenses !== null ? `${formatFCFA(totalDepenses)} FCFA` : "—",
        meta: walletAccessBlocked
          ? t("dashboard.metric.walletRestricted")
          : walletLocked
            ? t("dashboard.metric.accountRequired")
            : totalDepenses !== null
              ? t("dashboard.metric.syncedFromApi")
              : t("dashboard.metric.noExpenses"),
        icon: "wallet",
      },
      {
        label: t("dashboard.metric.avgRating"),
        value: averageRating !== null ? `${averageRating.toFixed(1)} / 5` : "—",
        meta: ratingCount > 0
          ? t("dashboard.metric.evaluations", { count: ratingCount })
          : averageRating !== null
            ? t("dashboard.metric.ratingSynced")
            : t("dashboard.metric.noRating"),
        icon: "star",
      },
      {
        label: t("dashboard.metric.loyaltyPoints"),
        value: loyaltyPoints !== null ? `${formatCount(loyaltyPoints)} pts` : "—",
        meta: loyaltyPoints !== null ? t("dashboard.metric.level", { level: loyaltyLevel }) : t("dashboard.metric.noPoints"),
        icon: "badge",
      },
    ];
  }, [averageRating, completedTripsCount, loyaltyLevel, loyaltyPoints, monthlyTrips, ratingCount, t, totalDepenses, walletAccessBlocked, walletLocked]);

  const heroBadges = useMemo(() => {
    const items = [];
    items.push({
      title: accountStatusLabel,
      tone: accountStatusTone,
    });
    items.push({
      title: lastRideLabel,
      tone: "is-soft",
    });
    items.push({
      title: dashboardMetaLabel,
      tone: "is-soft",
    });
    return items;
  }, [accountStatusLabel, accountStatusTone, dashboardMetaLabel, lastRideLabel]);

  const badges = useMemo(() => {
    const items = [];

    if (completedTripsCount >= 10) {
      items.push({
        title: t("dashboard.badge.regularDriver"),
        desc: t("dashboard.badge.tripsDone", { count: formatCount(completedTripsCount) }),
        tone: "green",
        icon: "crown",
      });
    }

    if (averageRating !== null) {
      items.push({
        title: averageRating >= 4.5 ? t("dashboard.badge.wellRated") : t("dashboard.badge.improving"),
        desc: ratingCount > 0
          ? t("dashboard.badge.ratingDetailWithCount", { rating: averageRating.toFixed(1), count: ratingCount })
          : t("dashboard.badge.ratingDetailSynced", { rating: averageRating.toFixed(1) }),
        tone: "blue",
        icon: "star",
      });
    }

    if (loyaltyPoints !== null) {
      items.push({
        title: t("dashboard.badge.loyalty"),
        desc: t("dashboard.badge.pointsAccumulated", { count: formatCount(loyaltyPoints) }),
        tone: "orange",
        icon: "heart",
      });
    }

    return items.slice(0, 3);
  }, [averageRating, completedTripsCount, loyaltyPoints, ratingCount, t]);

  return (
    <>
      <div className="dbc-hero">
        <div className="dbc-hero-copy">
          <span className="dbc-hero-kicker">{t("dashboard.hero.kicker")}</span>
          <h1>{t("dashboard.hero.greeting", { name: displayName })}</h1>
          <p>{t("dashboard.hero.subtitle")}</p>
          <div className="dbc-hero-meta">
            {heroBadges.map((badge) => (
              <span key={badge.title} className={`dbc-hero-chip ${badge.tone || ""}`.trim()}>
                {badge.title}
              </span>
            ))}
          </div>
          {dashboardError ? <p className="dbc-dashboard-error">{dashboardError}</p> : null}
        </div>

        <div className="dbc-hero-actions">
          <Link to="/course-driver" className="dbc-reserve-btn">
            {t("dashboard.hero.createTrip")}
            <PlusCircleIcon />
          </Link>
        
        </div>
      </div>

      <div className="dbc-metrics">
        {dashboardMetrics.map((item) => (
          <MetricCard
            key={item.label}
            item={item}
            locked={item.icon === "wallet" && walletLocked}
            blocked={item.icon === "wallet" && walletAccessBlocked}
          />
        ))}
      </div>

      <div className="dbc-middle-grid">
        <NextTripCard trip={nextTrip} loading={dashboardLoading && !normalizedRides.length} />
        <WalletCard
          blocked={walletAccessBlocked}
          locked={walletLocked}
          status={walletStatus}
          balance={walletBalance}
          balanceLoading={balanceLoading}
        />
        <BadgesCard badges={badges} />
      </div>

      <div className="dbc-bottom-grid">
        <HistoryCard rides={recentRides} />
        <DestinationsCard destinations={destinationStats} />
        <RatingsCard averageRating={averageRating} ratingCount={ratingCount} ratings={ratingBreakdown} />
      </div>
    </>
  );
}